import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { formatDuration } from '../duration'
import { CONTROL_RADIUS } from '../detailControls'
import QualityFeatureGroups from './QualityFeatureGroups'
import { isBlank } from '../detailShared'

// S427 / S428 / S429 (plan «Workflow teórico · Progress real», 23/09) — las tres pestañas TEÓRICAS del
// desplegable de Workflow: Tareas, Recursos y Calidad. Salen de su propia puerta
// (`…/operations/{job}/theoretical`), pedida al desplegar, y conviven con Preparación, que sale de N4.

// ⚠️ SE PLIEGA LO LARGO, NO SE PAGINA (decisión del PO). Máximos medidos por operación: 17 tareas, 75
// recursos, 118 características. Los primeros se ven, y el botón dice CUÁNTOS hay en total: plegar
// no es esconder, y una lista recortada sin decirlo se leería como si fuera toda.
const ROWS_SHOWN_FOLDED = 10

const useFolded = (rows) => {
  const [open, setOpen] = useState(false)
  const folds = rows.length > ROWS_SHOWN_FOLDED
  return { visible: folds && !open ? rows.slice(0, ROWS_SHOWN_FOLDED) : rows, folds, open, toggle: () => setOpen((prev) => !prev) }
}

const FoldToggle = ({ testId, fold, total }) => {
  const { t_ } = useSmartTranslation()
  if (!fold.folds) return null
  return (
    <Button data-testid={testId} size='small' onClick={() => fold.toggle()} sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: CONTROL_RADIUS }}>
      {fold.open ? t_('Show fewer') : t_('Show all {{count}}', { count: total })}
    </Button>
  )
}

// S427 — cada tarea con su tiempo teórico.
// ⚠️⚠️ S425 (enmendado en el hub el 23/09, `d1d557d`): la pestaña enseña el total POR PIEZA, y es el
// que trae N3 para la FILA — leído de ahí, NUNCA sumado aquí de las tareas. Una cifra, un origen:
// si la suma de las tareas y el campo de N3 no cuadran, lo que se enseña es el de N3, y la
// discrepancia es un hecho del servidor, no algo que esta pantalla tape recalculando.
const TasksTab = ({ tasks, row }) => {
  const perPieceSeconds = row.theoreticalDurationSeconds
  const { t_ } = useSmartTranslation()
  const fold = useFolded(tasks)
  if (tasks.length === 0) return <Typography data-testid='theoretical-tasks-empty' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No planned tasks')}</Typography>
  return (
    <Stack spacing={1}>
      <Table size='small' data-testid='theoretical-tasks'>
        <TableHead>
          <TableRow>
            <TableCell>{t_('Order')}</TableCell>
            <TableCell>{t_('Task')}</TableCell>
            <TableCell align='right'>{t_('Theoretical time')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* El orden es el que sirve Sun (`order`), y no se toca. */}
          {fold.visible.map((task) => (
            <TableRow key={task.taskReferenceId} data-testid={`theoretical-task-${task.taskReferenceId}`}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{isBlank(task.order) ? '—' : task.order}</TableCell>
              <TableCell data-testid='theoretical-task-name'>{isBlank(task.name) ? t_('Not reported') : task.name}</TableCell>
              <TableCell data-testid='theoretical-task-time' align='right' sx={{ fontFamily: 'monospace' }}>
                {isBlank(task.theoreticalDurationSeconds) ? t_('Not reported') : formatDuration(task.theoreticalDurationSeconds)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <FoldToggle testId='theoretical-tasks-toggle' fold={fold} total={tasks.length} />
      <Typography data-testid='theoretical-tasks-total-per-piece' variant='body2' sx={{ fontWeight: 700 }}>
        {`${t_('Total per piece')}: ${isBlank(perPieceSeconds) ? t_('Not reported') : formatDuration(perPieceSeconds)}`}
      </Typography>
      <OrderTotal row={row} />
    </Stack>
  )
}

// S443 — el TOTAL DE LA ORDEN y su desglose: tareas × cantidad, más la preparación inicial. Las tres
// cifras vienen de N3; NINGUNA se calcula aquí —ni siquiera el producto de la primera línea, que se
// enseña como factores—. Si la suma de Sun no cuadrara con el desglose, lo que se ve es la de Sun.
const OrderTotal = ({ row }) => {
  const { t_ } = useSmartTranslation()
  const notReported = t_('Not reported')
  const duration = (value) => (isBlank(value) ? notReported : formatDuration(value))
  // Preparación: «no gestionada» es un hecho (null servido); lo no servido, un hueco. Nunca un 0.
  const preparation = !row.initialPreparationServed
    ? notReported
    : row.initialPreparationManaged ? formatDuration(row.initialPreparationSeconds) : t_('Preparation not managed')
  return (
    <Box data-testid='theoretical-order-total'>
      <Typography data-testid='theoretical-order-total-value' variant='body2' sx={{ fontWeight: 700 }}>
        {`${t_('Total of the order')}: ${duration(row.theoreticalOrderDurationSeconds)}`}
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Typography data-testid='theoretical-order-total-tasks' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
          {`${t_('Tasks × {{quantity}}', { quantity: isBlank(row.nominalQuantity) ? '—' : row.nominalQuantity })}: ${duration(row.theoreticalDurationSeconds)}`}
        </Typography>
        <Typography data-testid='theoretical-order-total-preparation' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
          {`+ ${t_('Initial preparation')}: ${preparation}`}
        </Typography>
      </Box>
    </Box>
  )
}

// S428 — los recursos teóricos AGRUPADOS POR TIPO, con la cantidad en PRIMERA UNIDAD (R-17). En la
// base del PO son todos de un tipo: saldrá un solo grupo, y es lo que hay.
const ResourceGroup = ({ group, index }) => {
  const { t_ } = useSmartTranslation()
  const fold = useFolded(group.resources)
  return (
    <Box data-testid={`theoretical-resource-group-${index}`}>
      <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
        {`${isBlank(group.type) ? t_('Not reported') : systemValueLabel(t_, 'ResourceTypeEnum', group.type)} · ${group.resources.length}`}
      </Typography>
      <Table size='small'>
        <TableBody>
          {fold.visible.map((resource) => (
            <TableRow key={resource.processResourceId} data-testid={`theoretical-resource-${resource.processResourceId}`}>
              {/* ⚠️ `name` vacío es un recurso que el CATÁLOGO NO RESUELVE: se dice así, no se deja
                  en blanco ni se inventa con el id. */}
              <TableCell data-testid='theoretical-resource-name' sx={{ color: isBlank(resource.name) ? 'text.secondary' : 'text.primary' }}>
                {isBlank(resource.name) ? t_('Not resolved by the catalog') : resource.name}
              </TableCell>
              <TableCell data-testid='theoretical-resource-quantity' align='right' sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {isBlank(resource.quantityFirstUnit) ? t_('Not reported') : `${resource.quantityFirstUnit}${isBlank(resource.unitCode) ? '' : ` ${resource.unitCode}`}`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <FoldToggle testId={`theoretical-resource-group-${index}-toggle`} fold={fold} total={group.resources.length} />
    </Box>
  )
}

const ResourcesTab = ({ groups }) => {
  const { t_ } = useSmartTranslation()
  if (groups.length === 0) return <Typography data-testid='theoretical-resources-empty' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No planned resources')}</Typography>
  return <Stack spacing={2} data-testid='theoretical-resources'>{groups.map((group, index) => <ResourceGroup key={`${group.type}-${index}`} group={group} index={index} />)}</Stack>
}

// S429 — las características a medir, POR TAREA (D5) y dentro en sus GRUPOS, como las pinta MOS
// (`QualityFeatureGroups`). Una tarea sin características no se pinta aquí: no tiene nada que medir,
// y su fila ya está en la pestaña de Tareas.
const characteristicsOf = (task) => (task.featureGroups || []).reduce((sum, group) => sum + group.characteristics.length, 0)

const QualityTab = ({ tasks }) => {
  const { t_ } = useSmartTranslation()
  const withCharacteristics = tasks.filter((task) => characteristicsOf(task) > 0)
  if (withCharacteristics.length === 0) return <Typography data-testid='theoretical-quality-empty' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No characteristics to measure')}</Typography>
  return (
    <Stack spacing={3} data-testid='theoretical-quality'>
      {withCharacteristics.map((task) => (
        <Box key={task.taskReferenceId} data-testid={`theoretical-quality-task-${task.taskReferenceId}`}>
          <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
            {`${isBlank(task.name) ? t_('Not reported') : task.name} · ${characteristicsOf(task)}`}
          </Typography>
          <QualityFeatureGroups featureGroups={task.featureGroups} mode='planned' testIdPrefix={`theoretical-quality-task-${task.taskReferenceId}`} />
        </Box>
      ))}
    </Stack>
  )
}

/**
 * Las tres pestañas teóricas, listas para `DetailTabs`, según el estado de su puerta.
 *
 * ⚠️ MIENTRAS CARGA, el recuento es «…», no un cero: un «0» diría que no hay tareas. Si la puerta
 * FALLA no se pintan —una pestaña vacía afirmaría lo mismo— y quien llama dice que no se pudo cargar.
 *
 * @param {{status: string, data: {tasks: Array, resourceGroups: Array}}} theoretical
 * @param {Object} row  la FILA de N3: el total por pieza (S425) y el de la orden con su desglose (S443).
 */
export const theoreticalTabs = (theoretical, t_, row = {}) => {
  if (theoretical.status === 'error') return []
  const loading = theoretical.status !== 'ready'
  const tasks = (theoretical.data || {}).tasks || []
  const groups = (theoretical.data || {}).resourceGroups || []
  const loadingContent = <Typography data-testid='theoretical-loading' variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
  return [
    { key: 'tasks', label: t_('Tasks'), count: loading ? '…' : tasks.length, content: loading ? loadingContent : <TasksTab tasks={tasks} row={row} /> },
    { key: 'resources', label: t_('Resources'), count: loading ? '…' : groups.reduce((sum, group) => sum + group.resources.length, 0), content: loading ? loadingContent : <ResourcesTab groups={groups} /> },
    { key: 'quality', label: t_('Quality'), count: loading ? '…' : tasks.reduce((sum, task) => sum + characteristicsOf(task), 0), content: loading ? loadingContent : <QualityTab tasks={tasks} /> }
  ]
}
